// Supported locales. The canonical set of language codes the platform
// translates into. Keep in sync with frontend/packages/i18n/src/locales/*.json
// and with docs/slo.md / the PRD's "30+ languages" commitment.
export const SUPPORTED_LOCALES = [
  "en",
  "ar",
  "bn",
  "de",
  "es",
  "fa",
  "fr",
  "ha",
  "he",
  "hi",
  "id",
  "it",
  "ja",
  "ko",
  "ms",
  "my",
  "ne",
  "nl",
  "pl",
  "pt",
  "ro",
  "ru",
  "sv",
  "sw",
  "ta",
  "te",
  "th",
  "tr",
  "uk",
  "ur",
  "vi",
  "yo",
  "zh-CN",
  "zh-TW",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

// Right-to-left locales. Platform UI direction flips for these.
export const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(["ar", "fa", "he", "ur"]);

export function isRtlLocale(locale: Locale): boolean {
  return RTL_LOCALES.has(locale);
}

export function isSupportedLocale(code: string): code is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/**
 * Resolve the active locale on app boot. Priority:
 *   1. `?lang=xx` URL query param (useful for QA / screenshots)
 *   2. `humaniti.locale` in localStorage (set by the user in the app)
 *   3. `navigator.language`
 *   4. DEFAULT_LOCALE
 *
 * Falls through each step if the value is not in SUPPORTED_LOCALES.
 */
export function detectLocale(
  win: Window | undefined = typeof window !== "undefined" ? window : undefined
): Locale {
  if (!win) return DEFAULT_LOCALE;

  // 1. URL query
  const params = new URLSearchParams(win.location.search);
  const queryLang = params.get("lang");
  if (queryLang) {
    const normalized = normalize(queryLang);
    if (normalized && isSupportedLocale(normalized)) return normalized;
  }

  // 2. localStorage
  try {
    const stored = win.localStorage?.getItem("humaniti.locale");
    if (stored) {
      const normalized = normalize(stored);
      if (normalized && isSupportedLocale(normalized)) return normalized;
    }
  } catch {
    // localStorage can throw in privacy mode; fall through
  }

  // 3. navigator
  const navLang = win.navigator?.language;
  if (navLang) {
    const normalized = normalize(navLang);
    if (normalized && isSupportedLocale(normalized)) return normalized;
  }

  return DEFAULT_LOCALE;
}

/**
 * Convert browser locale tags (e.g., "en-US", "pt-BR", "zh-Hans") to the
 * closest supported locale code. Returns `null` if no mapping is sensible.
 */
function normalize(raw: string): Locale | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Exact match first
  if (isSupportedLocale(trimmed)) return trimmed;

  // Chinese variants -> zh-CN / zh-TW
  if (trimmed.startsWith("zh")) {
    const lower = trimmed.toLowerCase();
    if (lower.includes("tw") || lower.includes("hant") || lower.includes("hk")) return "zh-TW";
    return "zh-CN";
  }

  // Drop region tag: "en-US" -> "en"
  const primary = trimmed.split(/[-_]/)[0].toLowerCase();
  if (isSupportedLocale(primary)) return primary as Locale;

  return null;
}
